<template lang="pug">
.ordered-resource-select.relative
  //- Two-column layout: 40% left (add), 60% right (list). Stack on small screens.
  .grid.grid-cols-1.md_grid-cols-5.gap-4.md_gap-6
    //- Left column: Add resource (relative for dropdown positioning)
    .md_col-span-2.space-y-2.relative
      label.block(v-if="label" :for="inputId")
        | {{ label }}
        span.text-red-500.required-asterisk(v-if="required") *
      p.text-sm.text-gray-500.dark_text-gray-400(v-if="instructionalText")
        | {{ instructionalText }}

      .dropdown-trigger.relative(ref="triggerRef")
        input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.text-gray-900.dark_text-white(
          :id="inputId"
          :value="searchQuery"
          @input="updateSearch"
          @focus="openDropdown"
          @keydown="handleKeydown"
          :placeholder="placeholder"
          :disabled="disabled"
          :class="{ 'opacity-50 cursor-not-allowed': disabled }"
          ref="inputRef"
        )
        button.dropdown-arrow(
          class="absolute right-2 top-1/2 -translate-y-1/2 p-1"
          @click="toggleDropdown"
          type="button"
          :disabled="disabled"
          :class="{ 'opacity-50 cursor-not-allowed': disabled }"
        )
          Icon(
            :icon="isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'"
            class="w-4 h-4 text-gray-500"
          )

      //- Dropdown menu (left column)
      .dropdown-menu.absolute.left-0.right-0.mt-1.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.shadow-lg.z-50.max-h-60.overflow-y-auto(
        v-if="isOpen"
        ref="dropdownRef"
      )
        .p-2
          .option-list.space-y-1
            .option.flex.items-center.gap-2.p-2.rounded.cursor-pointer.transition-colors.duration-150.text-gray-900.dark_text-white.hover_bg-gray-100.hover_text-gray-900.dark_hover_bg-gray-600.dark_hover_text-white(
              v-for="(resource, idx) in filteredResources"
              :key="resource.id"
              @click="addResource(resource)"
              @mouseenter="highlightedIndex = idx"
              :class="{ 'bg-gray-100 dark_bg-gray-600': highlightedIndex === idx }"
            )
              span.text-sm {{ resource.resourceId }} - {{ resource.title }}

          .no-results.p-2.text-sm.text-gray-500.text-center(
            v-if="filteredResources.length === 0"
          )
            | No resources found

    //- Right column: Ordered list
    .md_col-span-3
      .list-header.mb-2
        span.font-medium.text-gray-700.dark_text-gray-300 Playlist order
      .list-container.min-h-24.rounded.border.border-gray-300.dark_border-gray-600.bg-gray-50.dark_bg-gray-800.p-2(
        ref="listRef"
      )
        template(v-if="orderedSelectedItems.length > 0")
          SlickList.slick-list.space-y-1(
            axis="y"
            tag="div"
            :use-drag-handle="true"
            v-model:list="orderedSelectedItems"
            @sort-start="dragging = true"
            @sort-end="onSortEnd"
          )
            SlickItem(
              class="slick-item flex items-center gap-2 w-full px-2 py-1.5 bg-white dark_bg-gray-700 border border-gray-200 dark_border-gray-600 rounded text-sm"
              v-for="(item, i) in orderedSelectedItems"
              :key="item.id"
              :index="i"
              tag="div"
              :disabled="disabled"
            )
              span.order-num(class="w-6 flex-shrink-0 text-gray-500 dark_text-gray-400") {{ i + 1 }}
              .drag-handle.flex.items-center.flex-shrink-0.cursor-grab.active_cursor-grabbing(
                v-if="!disabled"
                v-handle="true"
                aria-label="Drag to reorder"
              )
                Icon(icon="mdi:drag" class="w-4 h-4 text-gray-500 dark_text-gray-300")
              span.truncate.flex-1.min-w-0(class="text-gray-900 dark_text-white" :title="`${item.resourceId} - ${item.title}`") {{ item.resourceId }} - {{ item.title }}
              .flex(class="flex-shrink-0 items-center gap-0.5")
                button.p-1.rounded(class="hover_bg-gray-200 dark_hover_bg-gray-600 transition-colors"
                  type="button"
                  aria-label="Move up"
                  :disabled="disabled || i === 0"
                  @click="moveUp(i)"
                )
                  Icon(icon="mdi:chevron-up" class="w-4 h-4 text-gray-600 dark_text-gray-300")
                button.p-1.rounded(class="hover_bg-gray-200 dark_hover_bg-gray-600 transition-colors"
                  type="button"
                  aria-label="Move down"
                  :disabled="disabled || i === orderedSelectedItems.length - 1"
                  @click="moveDown(i)"
                )
                  Icon(icon="mdi:chevron-down" class="w-4 h-4 text-gray-600 dark_text-gray-300")
                button.ml-auto.p-1.rounded(class="hover_bg-red-100 dark_hover_bg-red-900 transition-colors"
                  type="button"
                  aria-label="Remove"
                  :disabled="disabled"
                  @click="removeResource(item)"
                )
                  Icon(icon="mdi:close" class="w-4 h-4 text-gray-500 dark_text-gray-300")
        .empty-state.p-4.text-center.text-sm(v-else class="text-gray-500 dark_text-gray-400")
          | No resources yet. Add one with the dropdown on the left.
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Icon } from '@iconify/vue';
import {
  SlickList,
  SlickItem,
  HandleDirective as vHandle
} from 'vue-slicksort';

export interface LibraryResource {
  id: string;
  resourceId: number;
  title: string;
}

interface Props {
  modelValue: number[];
  resources: LibraryResource[];
  placeholder?: string;
  label?: string;
  instructionalText?: string;
  required?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Search for resources...',
  label: '',
  instructionalText: '',
  required: false,
  disabled: false
});

const emit = defineEmits<{
  'update:modelValue': [value: number[]];
}>();

const isOpen = ref(false);
const searchQuery = ref('');
const highlightedIndex = ref(-1);
const inputRef = ref<HTMLInputElement>();
const dropdownRef = ref<HTMLElement>();
const triggerRef = ref<HTMLElement>(); // input + arrow, so clicking arrow doesn't close
const listRef = ref<HTMLElement>();
const dragging = ref(false);

const inputId = `ordered-resource-select-${Math.random().toString(36).slice(2, 9)}`;

// Local ordered list of selected items (objects with id, resourceId, title) - synced from modelValue
const orderedSelectedItems = ref<LibraryResource[]>([]);

function buildOrderedItems(): LibraryResource[] {
  const ids = Array.isArray(props.modelValue) ? props.modelValue : [];
  if (ids.length === 0) return [];
  return ids
    .map(resourceId => {
      const r = props.resources.find(x => x.resourceId === resourceId);
      if (r) return r;
      return {
        id: `placeholder-${resourceId}`,
        resourceId,
        title: `Resource ${resourceId}`
      } as LibraryResource;
    })
    .filter(Boolean);
}

// Sync from parent when modelValue or resources change
watch(
  () => [props.modelValue, props.resources] as const,
  () => {
    orderedSelectedItems.value = buildOrderedItems();
  },
  { deep: true }
);

// Emit when user changes order (add, remove, or drag) - skip if order matches modelValue (e.g. after sync from parent)
watch(
  orderedSelectedItems,
  () => {
    const newIds = orderedSelectedItems.value.map(item => item.resourceId);
    const prev = Array.isArray(props.modelValue) ? props.modelValue : [];
    if (
      prev.length !== newIds.length ||
      prev.some((id, i) => id !== newIds[i])
    ) {
      emit('update:modelValue', newIds);
    }
  },
  { deep: true }
);

// Initial sync from modelValue
orderedSelectedItems.value = buildOrderedItems();

const filteredResources = computed(() => {
  if (!props.resources?.length) return [];
  const selectedIds = new Set(
    orderedSelectedItems.value.map(item => item.resourceId)
  );
  let list = props.resources.filter(r => !selectedIds.has(r.resourceId));
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(
      r => r.title.toLowerCase().includes(q) || String(r.resourceId).includes(q)
    );
  }
  return list;
});

function openDropdown() {
  if (!props.disabled) {
    isOpen.value = true;
    nextTick(() => inputRef.value?.focus());
  }
}

function closeDropdown() {
  isOpen.value = false;
  highlightedIndex.value = -1;
}

function toggleDropdown() {
  if (isOpen.value) closeDropdown();
  else openDropdown();
}

function updateSearch(event: Event) {
  const target = event.target as HTMLInputElement;
  searchQuery.value = target.value;
  highlightedIndex.value = -1;
}

function addResource(resource: LibraryResource) {
  orderedSelectedItems.value = [...orderedSelectedItems.value, resource];
}

function removeResource(item: LibraryResource) {
  orderedSelectedItems.value = orderedSelectedItems.value.filter(
    x => x.resourceId !== item.resourceId
  );
}

function moveUp(index: number) {
  if (index <= 0 || props.disabled) return;
  const list = [...orderedSelectedItems.value];
  [list[index - 1], list[index]] = [list[index], list[index - 1]];
  orderedSelectedItems.value = list;
}

function moveDown(index: number) {
  if (index >= orderedSelectedItems.value.length - 1 || props.disabled) return;
  const list = [...orderedSelectedItems.value];
  [list[index], list[index + 1]] = [list[index + 1], list[index]];
  orderedSelectedItems.value = list;
}

function onSortEnd() {
  dragging.value = false;
}

function handleKeydown(event: KeyboardEvent) {
  if (!isOpen.value) return;
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        filteredResources.value.length - 1
      );
      break;
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
      break;
    case 'Enter':
      event.preventDefault();
      if (
        highlightedIndex.value >= 0 &&
        highlightedIndex.value < filteredResources.value.length
      ) {
        addResource(filteredResources.value[highlightedIndex.value]);
      }
      break;
    case 'Escape':
      closeDropdown();
      break;
  }
}

function handleClickOutside(event: Event) {
  const target = event.target as Node;
  if (
    isOpen.value &&
    !dropdownRef.value?.contains(target) &&
    !triggerRef.value?.contains(target)
  ) {
    closeDropdown();
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.slick-item {
  list-style: none;
}
</style>
